import {
    useEffect, useRef, useCallback, useMemo,
} from 'react';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import { config } from 'config';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import { Deal } from '../../types';
import {
    MARKER, CONFLICT_MARKER, CONFLICT_COLOR, MAP_THEME, MAP_BEHAVIOR, MAP_LAYERS, MAP_SOURCE_ID, buildMarkerSvg,
} from '../components/mapTheme';
import { DealHoverPopupController } from '../components/dealHoverPopup';
import { DEAL_GROUP_PALETTE } from '../../constants';
import { DealColorLookup } from './useColoredDealIds';
import {
    type DealCoordinates,
    getDealCoords,
    buildGeoJSON,
    getBoundsFromGeoJSON,
} from '../../dealsMapGeoJson';

mapboxgl.accessToken = config.mapboxGlApiKey;

const loadSvgAsImage = (svgString: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
});

interface UseDealsMapboxMapArgs {
    deals: Deal[];
    geocodedCoordinates: Record<number, DealCoordinates>;
    selectedDealId: number | null;
    hoveredDealId: number | null;
    colorByDealId?: DealColorLookup;
    onSelectDeal: (deal: Deal) => void;
    onDeselectDeal: () => void;
    onHoverDeal: (dealId: number | null) => void;
}

export const useDealsMapboxMap = ({
    deals,
    geocodedCoordinates,
    selectedDealId,
    hoveredDealId,
    colorByDealId,
    onSelectDeal,
    onDeselectDeal,
    onHoverDeal,
}: UseDealsMapboxMapArgs) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const dealHoverPopupRef = useRef(new DealHoverPopupController());
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dealsRef = useRef<Deal[]>(deals);
    const geocodedRef = useRef<Record<number, DealCoordinates>>(geocodedCoordinates);
    const colorByDealIdRef = useRef<DealColorLookup | undefined>(colorByDealId);
    const onSelectDealRef = useRef(onSelectDeal);
    const onDeselectDealRef = useRef(onDeselectDeal);
    const onHoverDealRef = useRef(onHoverDeal);
    const didInitialBoundsFitRef = useRef(false);

    dealsRef.current = deals;
    geocodedRef.current = geocodedCoordinates;
    colorByDealIdRef.current = colorByDealId;
    onSelectDealRef.current = onSelectDeal;
    onDeselectDealRef.current = onDeselectDeal;
    onHoverDealRef.current = onHoverDeal;

    const geojson = useMemo(
        () => buildGeoJSON(deals, geocodedCoordinates, colorByDealId),
        [deals, geocodedCoordinates, colorByDealId],
    );

    // ── Event handlers ──────────────────────────────────────────────────

    const handleFitAll = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const bounds = getBoundsFromGeoJSON(buildGeoJSON(dealsRef.current, geocodedRef.current, colorByDealIdRef.current));
        if (bounds) {
            map.fitBounds(bounds, { padding: MAP_BEHAVIOR.fitBoundsPadding, maxZoom: MAP_BEHAVIOR.maxZoom });
        }
        onDeselectDealRef.current();
    }, []);

    // ── Map init & teardown ─────────────────────────────────────────────

    useEffect(() => {
        if (!containerRef.current) return;

        const dealHoverPopup = dealHoverPopupRef.current;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/dark-v10',
            center: [DEFAULT_NYC_COORDINATES.lng, DEFAULT_NYC_COORDINATES.lat],
            zoom: MAP_BEHAVIOR.minZoom,
            attributionControl: false,
            pitch: 0,
            maxPitch: 60,
        });

        mapRef.current = map;

        const handlePinClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
            if (!e.features || e.features.length === 0) return;
            e.originalEvent.stopPropagation();
            const dealId = e.features[0].properties?.id;
            const deal = dealsRef.current.find(d => d.id === dealId);
            if (deal) onSelectDealRef.current(deal);
        };

        const handleClusterClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
            if (!e.features || e.features.length === 0 || !mapRef.current) return;
            const feature = e.features[0];
            const clusterId = feature.properties?.cluster_id;
            const source = mapRef.current.getSource(MAP_SOURCE_ID) as GeoJSONSource;
            source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || zoom == null || !mapRef.current) return;
                const geometry = feature.geometry as GeoJSON.Point;
                mapRef.current.easeTo({
                    center: geometry.coordinates as [number, number],
                    zoom,
                });
            });
        };

        const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
            if (!mapRef.current) return;
            const features = mapRef.current.queryRenderedFeatures(e.point, {
                layers: [MAP_LAYERS.unclusteredPoint, MAP_LAYERS.clusters, MAP_LAYERS.clusterCount],
            });
            if (features.length === 0) {
                onDeselectDealRef.current();
            }
        };

        const handlePinMouseEnter = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
            if (!mapRef.current || !e.features || e.features.length === 0) return;
            const mapInstance = mapRef.current;
            mapInstance.getCanvas().style.cursor = 'pointer';
            const feature = e.features[0];
            const dealId = feature.properties?.id;
            if (dealId != null) onHoverDealRef.current(dealId);
            dealHoverPopupRef.current.show(mapInstance, feature);
        };

        const handlePinMouseLeave = () => {
            if (!mapRef.current) return;
            mapRef.current.getCanvas().style.cursor = '';
            onHoverDealRef.current(null);
            dealHoverPopupRef.current.remove();
        };

        const handleClusterMouseEnter = () => {
            if (!mapRef.current) return;
            mapRef.current.getCanvas().style.cursor = 'pointer';
        };

        const handleClusterMouseLeave = () => {
            if (!mapRef.current) return;
            mapRef.current.getCanvas().style.cursor = '';
        };

        map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right');

        map.on('load', async () => {
            const markerImg = await loadSvgAsImage(MARKER);
            map.addImage('marker', markerImg, { sdf: false });

            await Promise.all(DEAL_GROUP_PALETTE.map(async (entry, idx) => {
                const coloredImg = await loadSvgAsImage(buildMarkerSvg(entry.hex, 'white'));
                map.addImage(`marker-${idx}`, coloredImg, { sdf: false });
            }));

            const conflictImg = await loadSvgAsImage(CONFLICT_MARKER);
            map.addImage(`marker-conflict`, conflictImg, { sdf: false });

            // 3D terrain
            map.addSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: MAP_BEHAVIOR.maxZoom,
            });
            map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });

            // 3D buildings
            const layers = map.getStyle().layers;
            const labelLayerId = layers?.find(
                layer => layer.type === 'symbol' && layer.layout?.['text-field'],
            )?.id;

            map.addLayer(
                {
                    id: MAP_LAYERS.buildings,
                    source: 'composite',
                    'source-layer': 'building',
                    filter: ['==', 'extrude', 'true'],
                    type: 'fill-extrusion',
                    minzoom: MAP_BEHAVIOR.minZoom,
                    paint: {
                        'fill-extrusion-color': '#aaa',
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-base': ['get', 'min_height'],
                        'fill-extrusion-opacity': 0.5,
                    },
                },
                labelLayerId,
            );

            const initialGeojson = buildGeoJSON(dealsRef.current, geocodedRef.current, colorByDealIdRef.current);

            const clusterProperties: Record<string, any> = {};
            DEAL_GROUP_PALETTE.forEach((_, idx) => {
                clusterProperties[`groupColor_${idx}`] = ['+', ['get', `groupColor_${idx}`]];
            });

            map.addSource(MAP_SOURCE_ID, {
                type: 'geojson',
                data: initialGeojson,
                cluster: true,
                clusterMaxZoom: MAP_BEHAVIOR.clusterMaxZoom,
                clusterRadius: MAP_THEME.clusterCircleRadius,
                clusterProperties,
            });

            map.addLayer({
                id: MAP_LAYERS.clusters,
                type: 'circle',
                source: MAP_SOURCE_ID,
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [
                        'case',
                        ...DEAL_GROUP_PALETTE.map((entry, idx) => [
                            ['>', ['get', `groupColor_${idx}`], 0],
                            entry.hex,
                        ]).flat(),
                        MAP_THEME.clusterColor,
                    ],
                    'circle-radius': MAP_THEME.clusterCircleRadius,
                },
            });

            map.addLayer({
                id: MAP_LAYERS.clusterCount,
                type: 'symbol',
                source: MAP_SOURCE_ID,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-size': MAP_THEME.clusterCountFontSize,
                },
            });

            map.addLayer({
                id: MAP_LAYERS.unclusteredPoint,
                type: 'symbol',
                source: MAP_SOURCE_ID,
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': [
                        'case',
                        ['==', ['get', 'groupColorIdx'], -1],
                        'marker',
                        ['==', ['get', 'colorHex'], CONFLICT_COLOR],
                        'marker-conflict',
                        ['concat', 'marker-', ['to-string', ['get', 'groupColorIdx']]],
                    ],
                    'icon-size': 1,
                    'icon-anchor': 'bottom',
                    'icon-allow-overlap': true,
                },
                paint: {
                    'icon-opacity': 1,
                },
            });

            map.on('click', MAP_LAYERS.unclusteredPoint, handlePinClick);
            map.on('click', MAP_LAYERS.clusters, handleClusterClick);
            map.on('click', handleMapClick);
            map.on('mouseenter', MAP_LAYERS.unclusteredPoint, handlePinMouseEnter);
            map.on('mouseleave', MAP_LAYERS.unclusteredPoint, handlePinMouseLeave);
            map.on('mouseenter', MAP_LAYERS.clusters, handleClusterMouseEnter);
            map.on('mouseleave', MAP_LAYERS.clusters, handleClusterMouseLeave);

            const bounds = getBoundsFromGeoJSON(initialGeojson);
            if (bounds) {
                map.fitBounds(bounds, { padding: MAP_BEHAVIOR.fitBoundsPadding, maxZoom: MAP_BEHAVIOR.maxZoom });
                didInitialBoundsFitRef.current = true;
            }
        });

        resizeObserverRef.current = new ResizeObserver(() => {
            if (resizeTimeoutRef.current) 
                clearTimeout(resizeTimeoutRef.current);
            
            resizeTimeoutRef.current = setTimeout(() => {
                resizeTimeoutRef.current = null;
                mapRef.current?.resize();
            }, 100);
        });
        resizeObserverRef.current.observe(containerRef.current);

        return () => {
            dealHoverPopup.remove();
            map.off('click', MAP_LAYERS.unclusteredPoint, handlePinClick);
            map.off('click', MAP_LAYERS.clusters, handleClusterClick);
            map.off('click', handleMapClick);
            map.off('mouseenter', MAP_LAYERS.unclusteredPoint, handlePinMouseEnter);
            map.off('mouseleave', MAP_LAYERS.unclusteredPoint, handlePinMouseLeave);
            map.off('mouseenter', MAP_LAYERS.clusters, handleClusterMouseEnter);
            map.off('mouseleave', MAP_LAYERS.clusters, handleClusterMouseLeave);
            resizeObserverRef.current?.disconnect();
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
            map.remove();
            mapRef.current = null;
        };
    }, []);

    // ── Reactive data updates ───────────────────────────────────────────

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const source = map.getSource(MAP_SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) return;
        
        source.setData(geojson);
        
        if (didInitialBoundsFitRef.current) return;

        const bounds = getBoundsFromGeoJSON(geojson);
        if (bounds) {
            map.fitBounds(bounds, { padding: MAP_BEHAVIOR.fitBoundsPadding, maxZoom: MAP_BEHAVIOR.maxZoom });
            didInitialBoundsFitRef.current = true;
        }
    }, [geojson]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.getLayer(MAP_LAYERS.unclusteredPoint)) return;
        const hasSelection = selectedDealId != null;

        if (selectedDealId != null) {
            map.setLayoutProperty(MAP_LAYERS.unclusteredPoint, 'icon-size', [
                'case',
                ['==', ['get', 'id'], selectedDealId], MAP_THEME.selectedIconSize,
                ['==', ['get', 'id'], hoveredDealId ?? -1], MAP_THEME.hoveredIconSize,
                1,
            ]);

            map.setPaintProperty(MAP_LAYERS.unclusteredPoint, 'icon-opacity', [
                'case',
                ['==', ['get', 'id'], selectedDealId], 1,
                ['==', ['get', 'id'], hoveredDealId ?? -1], MAP_THEME.hoveredOpacity,
                MAP_THEME.nonSelectedOpacity,
            ]);
        } else {
            map.setLayoutProperty(MAP_LAYERS.unclusteredPoint, 'icon-size', [
                'case',
                ['==', ['get', 'id'], hoveredDealId ?? -1], MAP_THEME.hoveredIconSize,
                1,
            ]);

            map.setPaintProperty(MAP_LAYERS.unclusteredPoint, 'icon-opacity', 1);
        }

        if (map.getLayer(MAP_LAYERS.clusters)) {
            map.setPaintProperty(MAP_LAYERS.clusters, 'circle-opacity', hasSelection ? MAP_THEME.nonSelectedOpacity : 1);
        }
        if (map.getLayer(MAP_LAYERS.clusterCount)) {
            map.setPaintProperty(MAP_LAYERS.clusterCount, 'text-opacity', hasSelection ? MAP_THEME.nonSelectedOpacity : 1);
        }
    }, [selectedDealId, hoveredDealId]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || selectedDealId == null) return;

        const deal = deals.find(d => d.id === selectedDealId);
        if (!deal) return;
        const coords = getDealCoords(deal, geocodedCoordinates);
        if (coords) {
            const focusZoom = Math.min(MAP_BEHAVIOR.selectedDealZoom, MAP_BEHAVIOR.maxZoom);
            map.flyTo({
                center: [coords.lng, coords.lat],
                zoom: Math.max(map.getZoom(), focusZoom),
                duration: MAP_BEHAVIOR.zoomDuration,
            });
        }
    }, [selectedDealId, deals, geocodedCoordinates]);

    return { containerRef, handleFitAll };
};
